import {
  createFrontendModule,
  type ExtensionDefinition,
  type IconElement,
} from '@backstage/frontend-plugin-api';
import {
  NavContentBlueprint,
  type NavContentComponentProps,
} from '@backstage/plugin-app-react';

import { Sidebar } from './components/sidebar/sidebar';
import { useActiveItemId } from './hooks/use-active-item-id';
import { useNavSlots } from './hooks/use-nav-slots';
import {
  type NavConfig,
  navConfigSchema,
  type PageNavItems,
} from './nav-config';

/** @public */
export interface SidebarContentComponentProps {
  /** The parsed `nav-content:app` config. */
  config: NavConfig;
  /** AppNav's pages. The sidebar takes the ones the config places. */
  navItems: PageNavItems<IconElement>;
}

/**
 * The NS sidebar: the pages AppNav delivers, placed by the extension config in
 * the primary slot, its sections, the utility slot and Other, with the item on
 * the current path marked.
 *
 * @public
 */
export function SidebarContentComponent({
  config,
  navItems,
}: SidebarContentComponentProps) {
  const slots = useNavSlots(config, navItems);
  const activeItemId = useActiveItemId(slots);
  return <Sidebar activeItemId={activeItemId} slots={slots} />;
}

// Explicit `ExtensionDefinition` annotation is required because the inferred
// type returned by `NavContentBlueprint.makeWithOverrides` includes types that
// resolve through `@backstage/frontend-defaults/node_modules/@backstage/frontend-plugin-api`,
// which TypeScript cannot name portably (TS2742). Annotating with the
// top-level `ExtensionDefinition` type breaks that internal reference.
export const SidebarContent: ExtensionDefinition =
  NavContentBlueprint.makeWithOverrides({
    configSchema: navConfigSchema,
    factory(originalFactory, { config }) {
      function ConfiguredSidebarContent({
        navItems,
      }: NavContentComponentProps) {
        return <SidebarContentComponent config={config} navItems={navItems} />;
      }
      return originalFactory({ component: ConfiguredSidebarContent });
    },
  });

/**
 * The navigation module of the app plugin. The app loads it as the default
 * export of this package through discovery.
 *
 * @public
 */
export const navigationModule = createFrontendModule({
  extensions: [SidebarContent],
  pluginId: 'app',
});
